<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NextInput from 'dashboard/components-next/input/Input.vue';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  knowledgeBase: { type: Object, default: null },
  portals: { type: Array, default: () => [] },
  categories: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();

const isEditMode = computed(() => Boolean(props.knowledgeBase));
const currentStep = ref(1);
const highestStep = ref(1);
const form = reactive({
  name: '',
  description: '',
  status: 'active',
  help_center_portal_id: '',
  help_center_category_id: '',
});

const steps = computed(() => [
  { number: 1, label: t('CORTEX.KNOWLEDGE.WIZARD.STEP_IDENTITY') },
  { number: 2, label: t('CORTEX.KNOWLEDGE.WIZARD.STEP_AUTOMATION') },
  { number: 3, label: t('CORTEX.KNOWLEDGE.WIZARD.STEP_HELP_CENTER') },
  { number: 4, label: t('CORTEX.KNOWLEDGE.WIZARD.STEP_REVIEW') },
]);

const statusOptions = computed(() => [
  { value: 'active', label: t('CORTEX.KNOWLEDGE.STATUS.active') },
  { value: 'inactive', label: t('CORTEX.KNOWLEDGE.STATUS.inactive') },
]);

const portalOptions = computed(() => [
  {
    value: '',
    label: t('CORTEX.KNOWLEDGE.CONFIG.HELP_CENTER_PORTAL_EMPTY'),
  },
  ...props.portals.map(portal => ({
    value: portal.id,
    label: portal.name,
  })),
]);

const categoryOptions = computed(() => [
  {
    value: '',
    label: t('CORTEX.KNOWLEDGE.CONFIG.HELP_CENTER_CATEGORY_EMPTY'),
  },
  ...props.categories
    .filter(
      category =>
        String(category.portal_id) === String(form.help_center_portal_id)
    )
    .map(category => ({
      value: category.id,
      label: category.name,
    })),
]);

const selectedStatusLabel = computed(
  () =>
    statusOptions.value.find(option => option.value === form.status)?.label ||
    form.status
);
const selectedPortalLabel = computed(
  () =>
    portalOptions.value.find(
      option => String(option.value) === String(form.help_center_portal_id)
    )?.label
);
const selectedCategoryLabel = computed(
  () =>
    categoryOptions.value.find(
      option => String(option.value) === String(form.help_center_category_id)
    )?.label
);

const canAdvance = computed(
  () => currentStep.value !== 1 || Boolean(form.name.trim())
);

const applyKnowledgeBase = knowledgeBase => {
  form.name = knowledgeBase?.name || '';
  form.description = knowledgeBase?.description || '';
  form.status = knowledgeBase?.status || 'active';
  form.help_center_portal_id =
    knowledgeBase?.settings?.help_center_portal_id || '';
  form.help_center_category_id =
    knowledgeBase?.settings?.help_center_category_id || '';
};

const goNext = () => {
  if (!canAdvance.value || currentStep.value >= steps.value.length) return;
  currentStep.value += 1;
  highestStep.value = Math.max(highestStep.value, currentStep.value);
};

const goBack = () => {
  if (currentStep.value === 1) {
    emit('close');
    return;
  }
  currentStep.value -= 1;
};

const goToStep = step => {
  if (step <= highestStep.value) currentStep.value = step;
};

const save = () => {
  if (!form.name.trim()) return;

  emit('save', {
    name: form.name.trim(),
    description: form.description.trim(),
    status: form.status,
    settings: {
      ...(props.knowledgeBase?.settings || {}),
      help_center_portal_id: form.help_center_portal_id || null,
      help_center_category_id: form.help_center_category_id || null,
    },
  });
};

watch(
  () => form.help_center_portal_id,
  (portalId, previousPortalId) => {
    if (previousPortalId && String(portalId) !== String(previousPortalId)) {
      form.help_center_category_id = '';
    }
  }
);

applyKnowledgeBase(props.knowledgeBase);

defineExpose({
  currentStep,
  form,
  isEditMode,
  goNext,
  goBack,
  save,
});
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-black1 backdrop-blur-sm"
    >
      <div
        class="flex flex-col w-full max-w-2xl mx-4 overflow-hidden border shadow-xl bg-n-surface-1 rounded-lg border-n-weak max-h-[90vh]"
      >
        <div
          class="flex items-center justify-between px-6 py-4 border-b border-n-weak shrink-0"
        >
          <div class="flex items-center gap-3">
            <span
              :class="isEditMode ? 'i-lucide-pencil' : 'i-lucide-book-plus'"
              class="size-5 text-n-brand"
            />
            <h2 class="text-lg font-semibold text-n-slate-12">
              {{
                isEditMode
                  ? t('CORTEX.KNOWLEDGE.WIZARD.TITLE_EDIT')
                  : t('CORTEX.KNOWLEDGE.WIZARD.TITLE_CREATE')
              }}
            </h2>
          </div>
          <button
            type="button"
            class="p-1.5 rounded-lg hover:bg-n-alpha-2"
            :aria-label="t('CORTEX.KNOWLEDGE.WIZARD.CLOSE')"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-5 text-n-slate-11" />
          </button>
        </div>

        <div
          class="flex items-center gap-1 px-6 py-3 overflow-x-auto border-b border-n-weak/50 shrink-0"
        >
          <template v-for="(step, index) in steps" :key="step.number">
            <button
              type="button"
              data-testid="cortex-knowledge-wizard-step"
              :disabled="step.number > highestStep"
              class="flex items-center gap-1.5 text-xs font-medium transition-colors shrink-0 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:cursor-default"
              :class="
                currentStep >= step.number
                  ? 'text-n-brand hover:text-n-brand/80'
                  : 'text-n-slate-10'
              "
              @click="goToStep(step.number)"
            >
              <span
                class="flex items-center justify-center font-bold transition-all rounded-full size-6 text-[10px]"
                :class="
                  currentStep >= step.number
                    ? 'bg-n-brand text-white'
                    : 'bg-n-alpha-2 text-n-slate-10'
                "
              >
                <span
                  v-if="currentStep > step.number"
                  class="i-lucide-check size-3.5"
                />
                <template v-else>{{ step.number }}</template>
              </span>
              <span class="hidden whitespace-nowrap sm:inline">
                {{ step.label }}
              </span>
            </button>
            <span
              v-if="index < steps.length - 1"
              class="flex-1 h-px mx-1 bg-n-weak min-w-2"
            />
          </template>
        </div>

        <div class="flex-1 px-6 py-5 overflow-y-auto">
          <div v-if="currentStep === 1" class="grid gap-5">
            <div>
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ t('CORTEX.KNOWLEDGE.WIZARD.IDENTITY_TITLE') }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX.KNOWLEDGE.WIZARD.IDENTITY_DESCRIPTION') }}
              </p>
            </div>

            <div>
              <label
                for="cortex-knowledge-wizard-name"
                class="block mb-1.5 text-sm font-medium text-n-slate-12"
              >
                {{ t('CORTEX.KNOWLEDGE.FORM.NAME') }}
              </label>
              <NextInput
                id="cortex-knowledge-wizard-name"
                v-model="form.name"
                class="w-full"
                :placeholder="t('CORTEX.KNOWLEDGE.FORM.NAME_PLACEHOLDER')"
                required
              />
              <p class="mt-1.5 text-xs text-n-slate-11">
                {{ t('CORTEX.KNOWLEDGE.WIZARD.NAME_HELP') }}
              </p>
            </div>

            <div>
              <label
                for="cortex-knowledge-wizard-description"
                class="block mb-1.5 text-sm font-medium text-n-slate-12"
              >
                {{ t('CORTEX.KNOWLEDGE.FORM.DESCRIPTION') }}
              </label>
              <TextArea
                id="cortex-knowledge-wizard-description"
                v-model="form.description"
                auto-height
                min-height="6rem"
                max-height="12rem"
                :placeholder="
                  t('CORTEX.KNOWLEDGE.FORM.DESCRIPTION_PLACEHOLDER')
                "
              />
              <p class="mt-1.5 text-xs text-n-slate-11">
                {{ t('CORTEX.KNOWLEDGE.WIZARD.DESCRIPTION_HELP') }}
              </p>
            </div>
          </div>

          <div v-if="currentStep === 2" class="grid gap-5">
            <div>
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ t('CORTEX.KNOWLEDGE.WIZARD.AUTOMATION_TITLE') }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX.KNOWLEDGE.WIZARD.AUTOMATION_DESCRIPTION') }}
              </p>
            </div>

            <div>
              <label class="block mb-1.5 text-sm font-medium text-n-slate-12">
                {{ t('CORTEX.KNOWLEDGE.CONFIG.STATUS') }}
              </label>
              <AdaptiveSelect
                v-model="form.status"
                :options="statusOptions"
                class="w-full"
              />
              <p class="mt-1.5 text-xs text-n-slate-11">
                {{ t('CORTEX.KNOWLEDGE.WIZARD.STATUS_HELP') }}
              </p>
            </div>
          </div>

          <div v-if="currentStep === 3" class="grid gap-5">
            <div>
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ t('CORTEX.KNOWLEDGE.WIZARD.HELP_CENTER_TITLE') }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX.KNOWLEDGE.WIZARD.HELP_CENTER_DESCRIPTION') }}
              </p>
            </div>

            <div>
              <label class="block mb-1.5 text-sm font-medium text-n-slate-12">
                {{ t('CORTEX.KNOWLEDGE.CONFIG.HELP_CENTER_PORTAL') }}
              </label>
              <AdaptiveSelect
                v-model="form.help_center_portal_id"
                :options="portalOptions"
                class="w-full"
              />
              <p class="mt-1.5 text-xs text-n-slate-11">
                {{ t('CORTEX.KNOWLEDGE.WIZARD.PORTAL_HELP') }}
              </p>
            </div>

            <div>
              <label class="block mb-1.5 text-sm font-medium text-n-slate-12">
                {{ t('CORTEX.KNOWLEDGE.CONFIG.HELP_CENTER_CATEGORY') }}
              </label>
              <AdaptiveSelect
                v-model="form.help_center_category_id"
                :options="categoryOptions"
                :disabled="!form.help_center_portal_id"
                class="w-full"
              />
              <p class="mt-1.5 text-xs text-n-slate-11">
                {{ t('CORTEX.KNOWLEDGE.WIZARD.CATEGORY_HELP') }}
              </p>
            </div>
          </div>

          <div v-if="currentStep === 4" class="grid gap-5">
            <div>
              <h3 class="text-sm font-medium text-n-slate-12">
                {{ t('CORTEX.KNOWLEDGE.WIZARD.REVIEW_TITLE') }}
              </h3>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CORTEX.KNOWLEDGE.WIZARD.REVIEW_DESCRIPTION') }}
              </p>
            </div>

            <dl class="grid gap-4">
              <div class="pb-4 border-b border-n-weak">
                <dt class="text-xs font-medium text-n-slate-10">
                  {{ t('CORTEX.KNOWLEDGE.FORM.NAME') }}
                </dt>
                <dd class="mt-1 text-sm font-medium text-n-slate-12">
                  {{ form.name }}
                </dd>
                <dd
                  v-if="form.description"
                  class="mt-1 text-sm text-n-slate-11"
                >
                  {{ form.description }}
                </dd>
              </div>
              <div class="grid gap-4 sm:grid-cols-2">
                <div>
                  <dt class="text-xs font-medium text-n-slate-10">
                    {{ t('CORTEX.KNOWLEDGE.CONFIG.STATUS') }}
                  </dt>
                  <dd class="mt-1 text-sm text-n-slate-12">
                    {{ selectedStatusLabel }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs font-medium text-n-slate-10">
                    {{ t('CORTEX.KNOWLEDGE.CONFIG.HELP_CENTER_PORTAL') }}
                  </dt>
                  <dd class="mt-1 text-sm text-n-slate-12">
                    {{ selectedPortalLabel }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs font-medium text-n-slate-10">
                    {{ t('CORTEX.KNOWLEDGE.CONFIG.HELP_CENTER_CATEGORY') }}
                  </dt>
                  <dd class="mt-1 text-sm text-n-slate-12">
                    {{ selectedCategoryLabel }}
                  </dd>
                </div>
              </div>
            </dl>
          </div>
        </div>

        <div
          class="flex items-center justify-between px-6 py-4 border-t border-n-weak shrink-0 bg-n-alpha-1"
        >
          <button
            type="button"
            class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-n-slate-11 rounded-lg hover:bg-n-alpha-2 transition-colors"
            @click="goBack"
          >
            <span class="i-lucide-arrow-left size-4" />
            {{
              currentStep === 1
                ? t('CORTEX.KNOWLEDGE.WIZARD.CANCEL')
                : t('CORTEX.KNOWLEDGE.WIZARD.BACK')
            }}
          </button>

          <button
            v-if="currentStep < steps.length"
            type="button"
            class="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:bg-n-brand/90 transition-colors disabled:opacity-50"
            :disabled="!canAdvance"
            @click="goNext"
          >
            {{ t('CORTEX.KNOWLEDGE.WIZARD.NEXT') }}
            <span class="i-lucide-arrow-right size-4" />
          </button>

          <button
            v-else
            type="button"
            class="flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white bg-n-brand rounded-lg hover:bg-n-brand/90 transition-colors disabled:opacity-50"
            :disabled="!form.name.trim() || isLoading"
            @click="save"
          >
            <span
              v-if="isLoading"
              class="i-lucide-loader-2 size-4 animate-spin"
            />
            <span
              v-else
              :class="isEditMode ? 'i-lucide-save' : 'i-lucide-book-plus'"
              class="size-4"
            />
            {{
              isEditMode
                ? t('CORTEX.KNOWLEDGE.WIZARD.SAVE')
                : t('CORTEX.KNOWLEDGE.WIZARD.CREATE')
            }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
